/**
 * Device API Client
 * Handles device registration, pairing, and management
 * 
 * CONSOLIDATED API LAYER - Single source of truth for device operations
 */

import axiosClient from './axiosClient';

/**
 * Normalize pairing code to XXX-XXX format
 * Backend expects "123-456", not "123456"
 * 
 * @param {string} code - Raw pairing code (e.g., "123456", "123-456", "123 456")
 * @returns {string} Normalized code in format "XXX-XXX"
 */
export const normalizePairingCode = (code) => {
  if (!code) return code;
  
  // Remove all non-digits
  const raw = code.toString().replace(/\D/g, '');
  
  // If exactly 6 digits, format as XXX-XXX
  if (raw.length === 6) {
    return `${raw.slice(0, 3)}-${raw.slice(3)}`;
  }
  
  // Fallback: return as-is if not 6 digits
  return code;
};

/**
 * Pair a device using pairing code
 * 
 * @param {Object} payload - Pairing payload
 * @param {string} payload.pairingCode - 6-digit pairing code from device
 * @param {string} payload.seniorId - ID of senior to assign device to
 * @param {string} [payload.deviceName] - Optional custom device name
 * @returns {Promise<Object>} Paired device object
 * 
 * @example
 * const device = await pairDevice({
 *   pairingCode: 'ABC123',
 *   seniorId: 'senior-uuid',
 *   deviceName: 'Oma Tablet'
 * });
 */
export async function pairDevice(payload) {
  const { pairingCode, seniorId, deviceName } = payload;
  
  // Validate required fields
  if (!pairingCode || !pairingCode.trim()) {
    throw new Error('Kopplungs-Code ist erforderlich.');
  }
  
  if (!seniorId) {
    throw new Error('Senior-Zuordnung ist erforderlich.');
  }
  
  // CRITICAL: Normalize pairing code to XXX-XXX format
  const normalizedCode = normalizePairingCode(pairingCode.trim());
  
  console.groupCollapsed('[Family][pairDevice]');
  console.log('Endpoint: POST /api/devices/pair_uuid');
  console.log('Raw pairing code:', pairingCode.trim());
  console.log('Normalized pairing code:', normalizedCode);
  console.log('Senior ID:', seniorId);
  console.log('Device name:', deviceName || '(auto-generate)');
  
  // Automated test: Verify normalization
  if (normalizedCode.includes('-') === false) {
    console.error('❌ Normalization failed for:', pairingCode);
  } else {
    console.log('✅ Normalization OK:', normalizedCode);
  }
  
  // Build request payload with normalized code
  const requestPayload = {
    pairing_code: normalizedCode,
    senior_id: seniorId
  };
  
  if (deviceName && deviceName !== '(auto)') {
    requestPayload.device_name = deviceName;
  }
  
  console.log('Request payload:', requestPayload);
  
  try {
    const response = await axiosClient.post('/api/devices/pair_uuid', requestPayload);
    
    console.log('✅ Status:', response.status);
    console.log('✅ Response data:', response.data);
    console.groupEnd();
    
    return response.data;
  } catch (error) {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    
    console.error('❌ Status:', status);
    console.error('❌ Error detail:', detail);
    console.error('❌ Full error:', error.response?.data);
    console.groupEnd();
    
    // Network error (no response)
    if (!error.response) {
      throw new Error('Netzwerkfehler. Bitte Internetverbindung prüfen.');
    }
    
    // Handle specific error codes with user-friendly messages
    switch (status) {
      case 404:
      case 422:
        throw new Error('Ungültiger Kopplungs-Code. Bitte prüfen Sie den Code auf dem Gerät.');
        
      case 409:
        throw new Error('Dieses Gerät ist bereits gekoppelt.');
        
      case 400:
        throw new Error('Der Kopplungs-Code ist abgelaufen. Bitte generieren Sie einen neuen Code.');
        
      case 403:
        throw new Error('Sie haben keine Berechtigung, Geräte zu koppeln.');
        
      case 405:
        throw new Error('Ungültiges Code-Format. System-Fehler - bitte Support kontaktieren.');
        
      default:
        if (status >= 500) {
          throw new Error('Serverfehler. Bitte später erneut versuchen.');
        }
        const message = detail || error.message || 'Unbekannter Fehler';
        throw new Error(message);
    }
  }
}

/**
 * Unpair a device
 * 
 * @param {string} deviceUUID - Device UUID to unpair
 * @returns {Promise<Object>} Success response
 */
export async function unpairDevice(deviceUUID) {
  if (!deviceUUID) {
    throw new Error('Device UUID ist erforderlich.');
  }
  
  console.groupCollapsed('[Family][unpairDevice]');
  console.log('Device UUID:', deviceUUID);
  console.log('Endpoint: DELETE /api/devices/uuid/{device_uuid}/unpair');
  
  try {
    const response = await axiosClient.delete(`/api/devices/uuid/${deviceUUID}/unpair`);
    
    console.log('✅ Status:', response.status);
    console.log('✅ Response data:', response.data);
    console.groupEnd();
    
    return response.data;
  } catch (error) {
    const status = error.response?.status;
    
    console.error('❌ Status:', status);
    console.error('❌ Error:', error.response?.data);
    console.groupEnd();
    
    if (status === 404) {
      throw new Error('Gerät nicht gefunden.');
    }
    
    if (status === 403) {
      throw new Error('Keine Berechtigung. Nur Admins können Geräte entkoppeln.');
    }
    
    throw new Error(error.response?.data?.detail || 'Fehler beim Entkoppeln des Geräts.');
  }
}

/**
 * Update device (name, senior_id)
 * Endpoint: PATCH /api/devices/{device_id}
 */
export async function updateDevice(deviceId, updateData) {
  console.groupCollapsed('[Family][updateDevice]');
  console.log('Device ID:', deviceId);
  console.log('Update data:', updateData);
  console.log('Endpoint: PATCH /api/devices/{device_id}');
  
  try {
    const response = await axiosClient.patch(`/api/devices/${deviceId}`, updateData);
    
    console.log('✅ Status:', response.status);
    console.log('✅ Response data:', response.data);
    console.groupEnd();
    
    return response.data;
  } catch (error) {
    const status = error.response?.status;
    
    console.error('❌ Status:', status);
    console.error('❌ Error:', error.response?.data);
    console.groupEnd();
    
    if (status === 404) {
      throw new Error('Gerät nicht gefunden.');
    }
    if (status === 403) {
      throw new Error('Keine Berechtigung für diese Aktion.');
    }
    
    throw new Error(error.response?.data?.detail || 'Fehler beim Aktualisieren des Geräts.');
  }
}

/**
 * Delete device (Admin only)
 * Endpoint: DELETE /api/devices/{device_id}
 */
export async function deleteDevice(deviceId) {
  console.groupCollapsed('[Family][deleteDevice]');
  console.log('Device ID:', deviceId);
  console.log('Endpoint: DELETE /api/devices/{device_id}');
  
  try {
    const response = await axiosClient.delete(`/api/devices/${deviceId}`);
    
    console.log('✅ Status:', response.status);
    console.log('✅ Response data:', response.data);
    console.groupEnd();
    
    return response.data;
  } catch (error) {
    const status = error.response?.status;
    
    console.error('❌ Status:', status);
    console.error('❌ Error:', error.response?.data);
    console.groupEnd();
    
    if (status === 404) {
      throw new Error('Gerät nicht gefunden.');
    }
    if (status === 403) {
      throw new Error('Keine Berechtigung. Nur Admins können Geräte löschen.');
    }
    
    throw new Error(error.response?.data?.detail || 'Fehler beim Löschen des Geräts.');
  }
}

/**
 * Start a test call from device to family member
 * Endpoint: POST /api/devices/test-call
 */
export async function startTestCall(deviceId, targetUserId) {
  try {
    const response = await axiosClient.post('/api/devices/test-call', {
      device_id: deviceId,
      target_user_id: targetUserId
    });
    
    console.log('[Family][startTestCall] ✅ Test call initiated');
    return response.data;
  } catch (error) {
    console.error('[Family][startTestCall] ❌ Failed:', error);
    throw new Error('Test-Anruf konnte nicht gestartet werden.');
  }
}
